import React, { startTransition, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import CatalogFilters from "../components/CatalogFilters";
import ProductCard from "../components/ProductCard";
import { fetchShopBrands, fetchShopCategories, fetchShopProductBatch } from "../api";
import useAutoLoadMore from "../useAutoLoadMore";
import { STORE_HOME_PATH } from "../../routes";


function applySearchParamUpdates(searchParams, updates) {
  const next = new URLSearchParams(searchParams);

  Object.entries(updates).forEach(([key, value]) => {
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
  });

  return next;
}


export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");

  const activeFilters = {
    category: searchParams.get("category") || "",
    brand: searchParams.get("brand") || "",
    sort: searchParams.get("sort") || "",
  };
  const activeSearch = searchParams.get("search") || "";

  useEffect(() => {
    let active = true;

    async function loadReferences() {
      try {
        const [categoryData, brandData] = await Promise.all([fetchShopCategories(), fetchShopBrands()]);

        if (!active) {
          return;
        }

        setCategories(categoryData);
        setBrands(brandData);
      } catch (requestError) {
        if (active) {
          setError("Filter ma'lumotlarini yuklashda xatolik yuz berdi.");
        }
      }
    }

    loadReferences();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    async function loadProducts() {
      setLoading(true);
      setLoadingMore(false);
      setError("");

      try {
        const batch = await fetchShopProductBatch({
          search: activeSearch,
          category: activeFilters.category,
          brand: activeFilters.brand,
          sort: activeFilters.sort,
        });

        if (!active) {
          return;
        }

        setProducts(batch.items);
        setHasMore(batch.hasMore);
      } catch (requestError) {
        if (active) {
          setError("Mahsulotlarni yuklashda xatolik yuz berdi.");
          setProducts([]);
          setHasMore(false);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadProducts();

    return () => {
      active = false;
    };
  }, [activeFilters.brand, activeFilters.category, activeFilters.sort, activeSearch]);

  const handleFilterChange = (field, value) => {
    startTransition(() => {
      setSearchParams(applySearchParamUpdates(searchParams, { [field]: value }));
    });
  };

  const handleClear = () => {
    startTransition(() => {
      const next = new URLSearchParams();
      if (activeSearch) {
        next.set("search", activeSearch);
      }
      setSearchParams(next, { replace: true });
    });
  };

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) {
      return;
    }

    setLoadingMore(true);
    setError("");

    try {
      const batch = await fetchShopProductBatch({
        search: activeSearch,
        category: activeFilters.category,
        brand: activeFilters.brand,
        sort: activeFilters.sort,
        offset: products.length,
      });

      setProducts((current) => [...current, ...batch.items]);
      setHasMore(batch.hasMore);
    } catch (requestError) {
      setError("Keyingi mahsulotlarni yuklashda xatolik yuz berdi.");
    } finally {
      setLoadingMore(false);
    }
  };

  const loadMoreRef = useAutoLoadMore({
    hasMore,
    isLoading: loading || loadingMore,
    onLoadMore: handleLoadMore,
  });

  return (
    <main className="store-page store-catalog-page">
      <section className="store-catalog-layout store-catalog-layout-inline">
        <div className="store-catalog-content">
          <div className="store-catalog-toolbar">
            <p className="store-section-kicker">Catalog</p>
            <Link className="store-outline-button" to={STORE_HOME_PATH}>
              Home ga qaytish
            </Link>
          </div>

          <CatalogFilters
            brands={brands}
            categories={categories}
            filters={activeFilters}
            onClear={handleClear}
            onFilterChange={handleFilterChange}
          />

          {loading ? (
            <div className="store-empty">Mahsulotlar yuklanmoqda...</div>
          ) : error && !products.length ? (
            <div className="store-empty">{error}</div>
          ) : products.length ? (
            <>
              <div className="store-product-grid store-product-grid-section">
                {products.map((product, index) => (
                  <ProductCard key={product.id} index={index} product={product} />
                ))}
              </div>

              {error ? <p className="store-error-text store-inline-error">{error}</p> : null}

              {hasMore ? <div ref={loadMoreRef} className="store-scroll-sentinel" /> : null}
              {loadingMore ? <p className="store-loading-more">Yana mahsulotlar yuklanmoqda...</p> : null}
            </>
          ) : (
            <div className="store-empty">
              Tanlangan filterlar bo'yicha mahsulot topilmadi. Filterlarni tozalab qayta urinib ko'ring.
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
